import { Injectable } from '@angular/core';
import { HealthScoreResponseModel } from 'src/app/api/proxy/datapoints/datapoints-models';
import { CurrentObservationStatusModel } from 'src/app/api/proxy/datapoints/models/current-observation-status-model';
import { EmotionsHistoryResponse } from 'src/app/api/proxy/emotion/emotion-models';
import { SummaryItemModel, SummaryModel } from 'src/app/api/proxy/wearables/wearables-models';
import { GenderType, ProfileModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import { EventService } from '../events/event.service';
import { HealthScoreHistory } from '../health-score-service/health-score-history';
import { MyProfileService } from '../my-profile-service/my-profile.service';
import { TranslatorService } from '../translator-service/translator.service';
import { filterGoalItems, formatGoalSliderValue } from 'src/app/shared/health-score/goal-format.helper';
import {
  buildGroupsFromBiometricDataPoints,
  getGroupRangeLevel,
  getHealthResultsLevelText,
} from 'src/app/shared/health-score/health-score-shared.helpers';
import { formatLocalizedDate } from 'src/app/controls/viva-date-input/date-format.util';

const PROMPT_BASE = 'health-score-ai-context-service.prompt';

@Injectable({
  providedIn: 'root',
})
export class HealthScoreAiContextService {
  private currentScore: HealthScoreResponseModel | null = null;
  private currentObservations: CurrentObservationStatusModel | null = null;
  private goalItems: SummaryItemModel[] = [];
  private emotionHistory: EmotionsHistoryResponse[] = [];
  private healthScoreHistory: HealthScoreHistory | null = null;
  private myProfile: ProfileModel | null = null;

  constructor(
    private _translator: TranslatorService,
    private _profileService: MyProfileService,
  ) {
    EventService.healthScoreUpdated.subscribe((healthscore) => {
      this.currentScore = healthscore;
    });
    EventService.onBiometricListUpdated.subscribe((data) => {
      this.currentObservations = data;
    });
    EventService.wearableSummaryUpdated.subscribe((summary: SummaryModel) => {
      this.goalItems = filterGoalItems(summary?.items);
    });
    EventService.emotionHistoryUpdated.subscribe((history: EmotionsHistoryResponse[]) => {
      this.emotionHistory = history;
    });
    EventService.healthScoreHistoryUpdated.subscribe((history: HealthScoreHistory) => {
      this.healthScoreHistory = history;
    });
    EventService.myProfileUpdated.subscribe((profile: ProfileModel) => {
      this.myProfile = profile;
    });
  }

  hasHealthScore(): boolean {
    return !!this.currentScore?.healthScore;
  }

  hasBiometricsContext(): boolean {
    return !!(
      this.currentObservations?.observations?.length ||
      this.goalItems.length ||
      this.healthScoreHistory?.items?.length
    );
  }

  buildBiometricsHumanQuestion(): string {
    const p = `${PROMPT_BASE}.biometrics`;
    const randomQuestion = this.pickRandomKey(`${p}.opening-question`, 5);
    let context = `${this.t(`${p}.context-header`)}\n\n`;

    if (this.currentObservations?.observations?.length) {
      context += `${this.t(`${p}.section-biometrics`)}\n`;
      const metrics = this.currentObservations.observations
        .slice(0, 10)
        .filter((obs) => obs.displayValue)
        .map((obs) => this.formatBiometricsMetricLine(obs, p));
      context += metrics.join('\n') + '\n\n';
    } else {
      context += `${this.t(`${p}.no-measurements`)}\n\n`;
    }

    if (this.goalItems.length) {
      context += `${this.t(`${p}.section-goals`)}\n`;
      this.goalItems.forEach((goal) => {
        if (goal.goalValue && goal.value) {
          const currentDisplay = formatGoalSliderValue(goal.value, goal, this.myProfile);
          const targetDisplay = formatGoalSliderValue(goal.goalValue, goal, this.myProfile);
          const achieved = goal.value >= goal.goalValue;
          context += this.t(`${p}.goal-line`, {
            name: goal.name,
            current: currentDisplay,
            target: targetDisplay,
            status: achieved ? this.t(`${p}.goal-achieved`) : this.t(`${p}.goal-in-progress`),
          }) + '\n';
        }
      });
      context += `\n`;
    }

    if (this.healthScoreHistory?.items?.length) {
      const recent = this.healthScoreHistory.items.slice(-5);
      context += `${this.t(`${p}.section-history`)}\n`;
      recent.forEach((item) => {
        const dateLabel = item.dateCalculated
          ? formatLocalizedDate(item.dateCalculated, this._translator.getCulture(), 'mediumDate')
          : this.t(`${p}.unknown-date`);
        context += this.t(`${p}.history-line`, { date: dateLabel, score: item.score }) + '\n';
      });
      context += `\n`;
    }

    const profileInfo = this.buildProfileInfoFragments(p, false);
    if (profileInfo.length) {
      context += this.t(`${p}.about-me`, { info: profileInfo.join(', ') }) + '\n\n';
    }

    context += randomQuestion;
    context += this.pickRandomKey(`${p}.follow-up`, 5);
    return context;
  }

  buildHumanQuestion(): string {
    const p = `${PROMPT_BASE}.health-score`;
    const randomQuestion = this.pickRandomKey(`${p}.opening-question`, 5);
    let context = `${this.t(`${p}.context-header`)}\n\n`;
    context += this.t(`${p}.score-line`, { score: this.currentScore?.healthScore });
    if (this.currentScore?.benchmarkScore) {
      context += this.t(`${p}.benchmark-suffix`, { benchmark: this.currentScore.benchmarkScore });
    }
    if (this.currentScore?.previousScore && this.currentScore?.healthScore) {
      const change = this.currentScore.healthScore - this.currentScore.previousScore;
      const changeAbs = Math.abs(change).toFixed(1);
      if (change > 0) {
        context += this.t(`${p}.trend-up`, { change: changeAbs, previousScore: this.currentScore.previousScore });
      } else if (change < 0) {
        context += this.t(`${p}.trend-down`, { change: changeAbs, previousScore: this.currentScore.previousScore });
      } else {
        context += this.t(`${p}.trend-same`, { previousScore: this.currentScore.previousScore });
      }
    }
    context += `.\n\n`;

    const groups = buildGroupsFromBiometricDataPoints(this.currentScore, this._translator);
    if (groups.length) {
      context += `${this.t(`${p}.section-groups`)}\n`;
      groups.forEach((group) => {
        if (group.hasData && group.score != null) {
          const rangeLevel = getGroupRangeLevel(this.currentScore, group);
          const levelText = getHealthResultsLevelText(this._translator, rangeLevel);
          const confidenceText = group.confidencePercentage
            ? this.t(`${p}.confidence`, { percentage: group.confidencePercentage })
            : '';
          context += this.t(`${p}.group-line`, {
            name: group.name,
            score: group.score,
            confidenceText,
            level: levelText,
          }) + '\n';
        }
      });
      context += `\n`;
    }

    if (this.goalItems.length) {
      context += `${this.t(`${p}.section-goals`)}\n`;
      this.goalItems.forEach((goal) => {
        if (goal.goalValue && goal.value) {
          const currentDisplay = formatGoalSliderValue(goal.value, goal, this.myProfile);
          const targetDisplay = formatGoalSliderValue(goal.goalValue, goal, this.myProfile);
          const achieved = goal.value >= goal.goalValue;
          context += this.t(`${p}.goal-line`, {
            name: goal.name,
            current: currentDisplay,
            target: targetDisplay,
            status: achieved ? this.t(`${p}.goal-achieved`) : this.t(`${p}.goal-in-progress`),
          }) + '\n';
        }
      });
      context += `\n`;
    }

    if (this.emotionHistory.length) {
      const emotion = this.emotionHistory[0];
      if (emotion?.emotionType && emotion?.loggedDate) {
        const emotionDate = new Date(emotion.loggedDate);
        const hoursAgo = Math.floor((Date.now() - emotionDate.getTime()) / (1000 * 60 * 60));
        context += this.t(`${p}.mood-logged`, { emotion: emotion.emotionType, hours: hoursAgo }) + '\n\n';
      }
    }

    if (this.currentObservations?.observations?.length) {
      context += `${this.t(`${p}.section-measurements`)}\n`;
      const metrics = this.currentObservations.observations
        .slice(0, 6)
        .filter((obs) => obs.displayValue)
        .map((obs) => this.formatHealthScoreMetricLine(obs, p));
      context += metrics.join('\n') + '\n\n';
    }

    const profileInfo = this.buildProfileInfoFragments(p, true);
    if (profileInfo.length) {
      context += this.t(`${p}.about-me`, { info: profileInfo.join(', ') }) + '\n\n';
    }

    context += randomQuestion;
    context += this.pickRandomKey(`${p}.follow-up`, 5);
    context += this.t(`${p}.response-format`);
    return context;
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this._translator.translateText(key, params);
  }

  private pickRandomKey(base: string, count: number): string {
    const index = Math.floor(Math.random() * count) + 1;
    return this.t(`${base}-${index}`);
  }

  private buildProfileInfoFragments(prefix: string, includeSmoking: boolean): string[] {
    const profileInfo: string[] = [];
    if (!this.myProfile) {
      return profileInfo;
    }
    const age = this._profileService.getAge(this.myProfile);
    if (age) {
      profileInfo.push(this.t(`${prefix}.age`, { age }));
    }
    if (this.myProfile.gender === GenderType.Male) {
      profileInfo.push(this.t(`${prefix}.gender`, {
        gender: this._translator.translateText('health-score-page.male'),
      }));
    } else if (this.myProfile.gender === GenderType.Female) {
      profileInfo.push(this.t(`${prefix}.gender`, {
        gender: this._translator.translateText('health-score-page.female'),
      }));
    }
    if (includeSmoking && this.myProfile.smoking != null) {
      profileInfo.push(
        this.myProfile.smoking ? this.t(`${prefix}.smoker`) : this.t(`${prefix}.non-smoker`),
      );
    }
    return profileInfo;
  }

  private formatBiometricsMetricLine(
    obs: NonNullable<CurrentObservationStatusModel['observations']>[number],
    prefix: string,
  ): string {
    return this.formatMetricLine(obs, prefix, 'trend-up', 'trend-down');
  }

  private formatHealthScoreMetricLine(
    obs: NonNullable<CurrentObservationStatusModel['observations']>[number],
    prefix: string,
  ): string {
    return this.formatMetricLine(obs, prefix, 'trend-up-short', 'trend-down-short');
  }

  private formatMetricLine(
    obs: NonNullable<CurrentObservationStatusModel['observations']>[number],
    prefix: string,
    trendUpKey: string,
    trendDownKey: string,
  ): string {
    const level = getHealthResultsLevelText(this._translator, obs.rangeLevel);
    const originalPreviousValue =
      (obs as { originalPreviousValue?: number }).originalPreviousValue ?? obs.previousValue;
    const originalValue = (obs as { originalValue?: number }).originalValue ?? obs.value;
    let trendText = '';
    if (originalPreviousValue != null && originalValue != null && originalPreviousValue !== originalValue) {
      trendText = originalPreviousValue < originalValue
        ? this.t(`${prefix}.${trendUpKey}`)
        : this.t(`${prefix}.${trendDownKey}`);
    }
    const prevVal = originalPreviousValue;
    const previousPart = prevVal != null
      ? this.t(`${prefix}.previous-part`, {
          value: typeof prevVal === 'number' ? prevVal : String(prevVal),
          unit: (obs as { unit?: string }).unit ? ` ${(obs as { unit?: string }).unit}` : '',
        })
      : '';
    return this.t(`${prefix}.metric-line`, {
      name: obs.name,
      displayValue: obs.displayValue,
      previousPart,
      trendText,
      level,
    });
  }
}
